import { useState } from 'react';
import { PreferenceCategoryContentResponseModel } from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { cn, useClientTranslation } from '@/lib/core';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { ProfilePreferencesList } from './list';
import { ProfilePreferencesSummary } from './summary';
import { PreferencePersonalizeSheet } from './sheets/personalize';

// ─── Shared entry type ────────────────────────────────────────────────────────

export interface PreferenceEntry {
  id: string;
  name: string;
  isSelected: boolean;
  membersOnly: boolean;
  isDisabled: boolean;
}

// ─── ProfilePreferences ───────────────────────────────────────────────────────

export interface SubcategorySection {
  id: string;
  name: string;
  items: PreferenceEntry[];
}

export interface ProfilePreferencesProps {
  /** CMS categories, rendered as the horizontal tab bar. */
  categories: PreferenceCategoryContentResponseModel[];
  activeCategoryId?: string;
  onCategoryChange?: (id: string) => void;
  /** Subcategory sections for the active category, each rendered with its own label and list. */
  subcategories: SubcategorySection[];
  /** When true, shows a spinner in place of the content lists while the active category loads. */
  loadingCategory?: boolean;
  /** Shown when the category has no items. */
  emptyLabel?: string;
  yourInterestsLabel?: string;
  /** Shown in the grey personalize banner. Omit to hide the banner. */
  personalizeText?: string;
  selectionCount: number;
  /** Pre-fills the personalize sheet with the user's existing value. */
  personalizedFashionInterest?: string | null;
  onPersonalizeSubmit?: (value: string | null) => Promise<void>;
  /** CMS overrides for the personalize sheet text. */
  personalizeSheetTitle?: string;
  personalizeSheetLabel?: string;
  personalizeSheetSaveLabel?: string;
  personalizeSheetCancelLabel?: string;
  onViewInterests?: () => void;
  onToggle: (id: string, isSelected: boolean) => Promise<void>;
}

export function ProfilePreferences({
  categories,
  activeCategoryId,
  onCategoryChange,
  subcategories,
  loadingCategory = false,
  emptyLabel,
  yourInterestsLabel,
  personalizeText,
  selectionCount,
  personalizedFashionInterest,
  onPersonalizeSubmit,
  personalizeSheetTitle,
  personalizeSheetLabel,
  personalizeSheetSaveLabel,
  personalizeSheetCancelLabel,
  onViewInterests,
  onToggle,
}: ProfilePreferencesProps) {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [personalizeSheetOpen, setPersonalizeSheetOpen] = useState(false);
  const t = useClientTranslation();
  const _emptyLabel = emptyLabel ?? t('App.Labels.PreferencesNoItems');
  const _yourInterestsLabel = yourInterestsLabel ?? t('App.Labels.YourInterests');

  const activeCategory = categories.find(c => c.id === activeCategoryId);
  const hasAlphabeticalSection = subcategories.some(sub => /^all brands$/i.test(sub.name));

  function handleViewInterests() {
    setSummaryOpen(true);
    onViewInterests?.();
  }

  return (
    <div className={cn('flex flex-col gap-6 px-4', hasAlphabeticalSection && 'pr-10.5')}>
      {/* Category tabs */}
      {categories.length > 0 && (
        <div className="scrollbar-hide relative z-20 -mx-4 overflow-x-auto px-4 pb-1">
          <div className="flex gap-4">
            {categories.map(cat => {
              const isActive = cat.id === activeCategoryId;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className="flex shrink-0 flex-col items-center gap-0.5"
                  onClick={() => onCategoryChange?.(cat.id)}
                >
                  <span
                    className={cn(
                      'font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] uppercase',
                      isActive ? 'font-semibold' : 'font-normal'
                    )}
                  >
                    {cat.name ?? ''}
                  </span>
                  <span className={cn('h-px w-4', isActive ? 'bg-jncquoi-neutral-1000' : 'bg-transparent')} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Personalize section — info card when value exists, prompt banner otherwise */}
      {/* Extends past the alphabetical index reserve (pr-10.5 on the wrapper) so it stays full width - 32px */}
      {personalizedFashionInterest ? (
        <div
          className={cn(
            'border-jncquoi-neutral-200 relative z-79 flex flex-col gap-2 border-b-[0.5px] pb-4',
            hasAlphabeticalSection && '-mr-6.5'
          )}
        >
          <div className="flex w-full items-center gap-2.5">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[14px] font-semibold tracking-[0.5px]">
              {t('App.Labels.PersonalizedFashionPreference')}
            </p>
            <button
              type="button"
              aria-label={t('App.Labels.EditFashionPreference')}
              className="shrink-0"
              onClick={() => setPersonalizeSheetOpen(true)}
            >
              <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          </div>
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
            {personalizedFashionInterest}
          </p>
        </div>
      ) : (
        <div
          className={cn(
            'bg-jncquoi-neutral-50 relative z-79 flex h-11 items-center gap-2.5 pr-1 pl-4',
            hasAlphabeticalSection && '-mr-6.5'
          )}
        >
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[14px] tracking-[0.5px]">
            {personalizeText}
          </p>
          <button
            type="button"
            aria-label={t('App.Labels.PersonalisePreferences')}
            className="flex size-11 items-center justify-center"
            onClick={() => setPersonalizeSheetOpen(true)}
          >
            <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
          </button>
        </div>
      )}

      {/* YOUR INTERESTS summary card */}

      {selectionCount > 0 && (
        <button
          type="button"
          className="bg-jncquoi-neutral-1000 flex h-14.25 items-center gap-4 px-4"
          onClick={handleViewInterests}
        >
          <Icon name="bookmark-solid" width={16} height={16} className="shrink-0 text-white" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-pp-neue-montreal text-[14px] font-medium tracking-[0.5px] text-white uppercase">
              {_yourInterestsLabel}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-400 overflow-hidden text-[12px] font-medium tracking-[0.5px] text-ellipsis whitespace-nowrap">
              {t('App.Labels.PreferencesSelectionCount', { count: selectionCount })}
            </span>
          </div>
          <div className="flex size-11 shrink-0 items-center justify-center">
            <Icon name="nav-arrow-right" width={20} height={20} className="text-white" />
          </div>
        </button>
      )}

      {/* Content lists */}
      {loadingCategory ? (
        <SpinLoading wrapperClassName="min-h-40" />
      ) : subcategories.every(s => s.items.length === 0) ? (
        <div className="flex min-h-40 items-center justify-center px-4">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-400 text-center text-[14px] tracking-[0.5px]">
            {_emptyLabel}
          </p>
        </div>
      ) : (
        <>
          {/* List Top */}
          <div className="relative flex flex-col gap-8 pb-8">
            {subcategories.map(sub =>
              sub.items.length > 0 ? (
                <section key={sub.id} className="flex flex-col overflow-hidden">
                  <div className="flex h-14.25 items-center">
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-7.25 tracking-[0.5px] uppercase">
                      {sub.name}
                    </span>
                  </div>
                  <ProfilePreferencesList
                    items={sub.items}
                    alphabetical={/^all brands$/i.test(sub.name)}
                    onToggle={onToggle}
                  />
                </section>
              ) : null
            )}
          </div>
        </>
      )}
      {summaryOpen && (
        <div className="pt-safe-area fixed inset-0 z-50">
          <ProfilePreferencesSummary
            yourInterestsLabel={_yourInterestsLabel}
            categoryName={activeCategory?.name ?? undefined}
            hintText={t('App.Labels.PreferencesRemoveHint')}
            subcategories={subcategories}
            onBack={() => setSummaryOpen(false)}
            onToggle={onToggle}
          />
        </div>
      )}

      <PreferencePersonalizeSheet
        open={personalizeSheetOpen}
        onOpenChange={setPersonalizeSheetOpen}
        initialValue={personalizedFashionInterest}
        title={personalizeSheetTitle}
        label={personalizeSheetLabel}
        saveLabel={personalizeSheetSaveLabel}
        cancelLabel={personalizeSheetCancelLabel}
        onSave={onPersonalizeSubmit ?? (() => Promise.resolve())}
      />
    </div>
  );
}
